import React, { useContext, useEffect, useState } from 'react'

import OrganizationsContext from 'containers/organizations/organizationsContext'
import ScreenLoading from 'components/ScreenLoading'
import FileUpload, { renderValidationError } from 'components/FileUpload/FileUpload'
import useListManualQueryDocuments from 'containers/manualQueries/useListManualQueryDocuments'
import useGetManualQueryDocument from 'containers/manualQueries/useGetManualQueryDocument'
import useReplaceManualQueryDocumentRows from 'containers/manualQueries/useReplaceManualQueryDocumentRows'
import useDeleteManualQueryDocument from 'containers/manualQueries/useDeleteManualQueryDocument'

import './ManualQueriesList.scss'

interface IManualQueryDocument {
  id: number
  label: string
  query_count: number
  countries: string[]
  is_active: boolean
  created_at: string
  crawl_exported_at: string | null
  metrics_exported_at: string | null
}

interface IQueryRow {
  query: string
  country: string
}

const COLUMNS_HINT =
  'CSV columns: query, country (both required). One row per query and country, ' +
  'country as a two-letter code such as gr or us.'

const TEMPLATE_CSV =
  'query,country\n' +
  'running shoes,gr\n' +
  'best trail shoes 2026,gr\n' +
  'running shoes,us\n'

function downloadTemplate() {
  const blob = new Blob([TEMPLATE_CSV], { type: 'text/csv' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = 'manual_queries_template.csv'
  link.click()
  URL.revokeObjectURL(url)
}

const runStamp = (value: string | null, isActive: boolean) => {
  if (!value) {
    return (
      <span className={`mq-chip ${isActive ? 'pending' : 'off'}`}>
        {isActive ? 'Pending' : '—'}
      </span>
    )
  }
  const d = new Date(value)
  return (
    <span className={`mq-chip ${isActive ? 'done' : 'off'}`}>
      {d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
    </span>
  )
}

const ManualQueriesList = () => {
  const { currentOrganization, isLoadingOrganization } =
    useContext(OrganizationsContext)
  const currentOrganizationId = currentOrganization.id

  const [data, fetchDocuments, fetchStatus, fetchError] =
    useListManualQueryDocuments(currentOrganizationId)
  const [detail, fetchDetail, detailStatus] = useGetManualQueryDocument(
    currentOrganizationId
  )
  const [, replaceRows, replaceStatus, replaceError] =
    useReplaceManualQueryDocumentRows(currentOrganizationId)
  const [, deleteDocument] = useDeleteManualQueryDocument(currentOrganizationId)

  const [openDocumentId, setOpenDocumentId] = useState<number | null>(null)
  const [showUpload, setShowUpload] = useState(false)
  const [editing, setEditing] = useState(false)
  const [editRows, setEditRows] = useState<IQueryRow[]>([])
  const [newQuery, setNewQuery] = useState('')
  const [newCountry, setNewCountry] = useState('')
  const [saveMessage, setSaveMessage] = useState('')

  useEffect(() => {
    fetchDocuments()
    // eslint-disable-next-line
  }, [currentOrganizationId])

  const documents: IManualQueryDocument[] = data?.documents || []
  const cap = data?.cap

  const closeDetail = () => {
    setOpenDocumentId(null)
    setEditing(false)
    setSaveMessage('')
  }

  const handleView = (doc: IManualQueryDocument) => {
    if (openDocumentId === doc.id) {
      closeDetail()
      return
    }
    setOpenDocumentId(doc.id)
    setEditing(false)
    setSaveMessage('')
    fetchDetail(doc.id)
  }

  const startEditing = () => {
    setEditRows((detail?.rows || []).map((r: IQueryRow) => ({ ...r })))
    setNewQuery('')
    setNewCountry('')
    setSaveMessage('')
    setEditing(true)
  }

  const removeRow = (index: number) => {
    setEditRows(editRows.filter((_, i) => i !== index))
  }

  const addRow = () => {
    const query = newQuery.trim().toLowerCase()
    const country = newCountry.trim().toLowerCase()
    if (!query || !country) {
      return
    }
    if (editRows.some((r) => r.query === query && r.country === country)) {
      setNewQuery('')
      setNewCountry('')
      return
    }
    setEditRows([...editRows, { query, country }])
    setNewQuery('')
    setNewCountry('')
  }

  const handleSave = async (doc: IManualQueryDocument) => {
    const result = await replaceRows(doc.id, editRows)
    if (result) {
      setEditing(false)
      setSaveMessage(
        'Saved. Changes take effect on the document’s next monthly run.'
      )
      fetchDetail(doc.id)
      fetchDocuments()
    }
  }

  const handleDelete = async (doc: IManualQueryDocument) => {
    const confirmed = window.confirm(
      `Delete "${doc.label}"? Its ${doc.query_count} queries stop being crawled from the next monthly run. ` +
        'Data already collected stays in your reports.'
    )
    if (!confirmed) {
      return
    }
    await deleteDocument(doc.id)
    closeDetail()
    fetchDocuments()
  }

  const handleUploadClosed = () => {
    setShowUpload(false)
    fetchDocuments()
  }

  const renderQueryList = (rows: IQueryRow[], isEditing: boolean) => (
    <ul className="mq-query-list">
      {rows.map((row, index) => (
        <li key={`${row.query}|${row.country}`}>
          <span className="mq-query">{row.query}</span>
          <span className="mq-chip cc">{row.country}</span>
          {isEditing && (
            <button
              className="mq-remove"
              title={`Remove ${row.query}`}
              onClick={() => removeRow(index)}
            >
              ×
            </button>
          )}
        </li>
      ))}
    </ul>
  )

  return (
    <div className="clusters-campaigns container-column manual-queries">
      {fetchError && (
        <div className="alert alert-danger" role="alert">
          {fetchError}
        </div>
      )}

      <div className="head">
        <div className="page-name">
          <h2>Manual Queries</h2>
          <span className="subtitle-text">
            Upload the search queries you want to track. Each document runs once
            a month: its queries are crawled on Google and their search volumes
            fetched, per country. Edits apply on the document’s next monthly run.
          </span>
          <div className="subtitle-text" style={{ marginTop: '10px' }}>
            <a
              href="#template"
              onClick={(event) => {
                event.preventDefault()
                downloadTemplate()
              }}
              title={COLUMNS_HINT}
            >
              Download the CSV import template
            </a>
            {cap && (
              <span className="mq-cap">
                {cap.used} of {cap.max} query–country pairs in use
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="head-actions">
        <button
          className="submit-button"
          onClick={() => setShowUpload(true)}
          title={COLUMNS_HINT}
        >
          <span className="enlarge">+</span>Upload document
        </button>
      </div>

      {isLoadingOrganization || (fetchStatus === 'pending' && !data) ? (
        <ScreenLoading />
      ) : documents.length === 0 ? (
        <div className="mq-empty">
          <p>
            No query documents yet. Upload a CSV with <code>query</code> and{' '}
            <code>country</code> columns to start tracking.
          </p>
        </div>
      ) : (
        <div className="mq-table-wrap">
          <table className="mq-table">
            <thead>
              <tr>
                <th>Document</th>
                <th>Queries</th>
                <th>Countries</th>
                <th>Crawl</th>
                <th>Volumes</th>
                <th>Status</th>
                <th aria-label="Actions"></th>
              </tr>
            </thead>
            <tbody>
              {documents.map((doc) => (
                <React.Fragment key={doc.id}>
                  <tr className={doc.is_active ? '' : 'retired'}>
                    <td data-label="Document" className="mq-label">
                      {doc.label}
                    </td>
                    <td data-label="Queries">{doc.query_count}</td>
                    <td data-label="Countries">
                      {(doc.countries || []).map((cc) => (
                        <span key={cc} className="mq-chip cc">
                          {cc}
                        </span>
                      ))}
                    </td>
                    <td data-label="Crawl">
                      {runStamp(doc.crawl_exported_at, doc.is_active)}
                    </td>
                    <td data-label="Volumes">
                      {runStamp(doc.metrics_exported_at, doc.is_active)}
                    </td>
                    <td data-label="Status">
                      <span
                        className={`mq-chip ${doc.is_active ? 'done' : 'off'}`}
                      >
                        {doc.is_active ? 'Active' : 'Retired'}
                      </span>
                    </td>
                    <td className="mq-actions">
                      <button className="mq-btn" onClick={() => handleView(doc)}>
                        {openDocumentId === doc.id ? 'Hide' : 'View'}
                      </button>
                      {doc.is_active && (
                        <button
                          className="mq-btn danger"
                          onClick={() => handleDelete(doc)}
                        >
                          Delete
                        </button>
                      )}
                    </td>
                  </tr>

                  {openDocumentId === doc.id && (
                    <tr className="mq-detail-row">
                      <td colSpan={7}>
                        {detailStatus === 'pending' && !editing ? (
                          <p className="mq-detail-loading">Loading queries…</p>
                        ) : editing ? (
                          <div className="mq-detail">
                            <div className="mq-detail-head">
                              <h4>
                                {doc.label} · editing {editRows.length} queries
                              </h4>
                              <div className="mq-detail-actions">
                                <button
                                  className="mq-btn"
                                  onClick={() => setEditing(false)}
                                >
                                  Cancel
                                </button>
                                <button
                                  className="submit-button small"
                                  disabled={replaceStatus === 'pending'}
                                  onClick={() => handleSave(doc)}
                                >
                                  {replaceStatus === 'pending'
                                    ? 'Saving…'
                                    : 'Save changes'}
                                </button>
                              </div>
                            </div>
                            {replaceError && (
                              <p className="mq-error">{renderValidationError(replaceError)}</p>
                            )}
                            {renderQueryList(editRows, true)}
                            <div className="mq-add-row">
                              <input
                                placeholder="new query"
                                value={newQuery}
                                onChange={(e) => setNewQuery(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && addRow()}
                              />
                              <input
                                className="mq-country-input"
                                placeholder="gr"
                                maxLength={3}
                                value={newCountry}
                                onChange={(e) => setNewCountry(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && addRow()}
                              />
                              <button className="mq-btn" onClick={addRow}>
                                Add
                              </button>
                              <span className="mq-hint">
                                Added queries run on the next monthly run.
                              </span>
                            </div>
                          </div>
                        ) : detail && detail.id === doc.id ? (
                          <div className="mq-detail">
                            <div className="mq-detail-head">
                              <h4>
                                {doc.label} · {detail.rows.length} queries
                              </h4>
                              {doc.is_active && (
                                <div className="mq-detail-actions">
                                  <button
                                    className="mq-btn"
                                    onClick={startEditing}
                                  >
                                    Edit queries
                                  </button>
                                </div>
                              )}
                            </div>
                            {saveMessage && (
                              <p className="mq-saved">{saveMessage}</p>
                            )}
                            {renderQueryList(detail.rows, false)}
                          </div>
                        ) : (
                          <p className="mq-detail-loading">
                            Could not load the document contents.
                          </p>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <FileUpload
        show={showUpload}
        onHide={handleUploadClosed}
        category="manual_search_term"
      />
    </div>
  )
}

export default ManualQueriesList
